import React from "react";
import { Empty } from "./OverviewTab";
import { useT } from "../../lib/i18n";

const ANNEX_II_KEYS = [
  { en: "Device description and specification", es: "Descripción y especificaciones del dispositivo", source: "device_identity" },
  { en: "Information supplied by the manufacturer", es: "Información proporcionada por el fabricante", source: "intended_purpose" },
  { en: "General safety and performance requirements", es: "Requisitos generales de seguridad y prestaciones", source: "existing_compliance" },
  { en: "Benefit-risk analysis and risk management", es: "Análisis beneficio-riesgo y gestión de riesgos", source: "risk" },
  { en: "Product verification and validation", es: "Verificación y validación del producto", source: "clinical" },
];
const ANNEX_III_KEYS = [
  { en: "Post-Market Surveillance plan", es: "Plan de vigilancia post-comercialización" },
  { en: "PSUR (Class IIa, IIb, III) / PMS Report (Class I)", es: "PSUR (Clase IIa/IIb/III) / Informe PMS (Clase I)" },
  { en: "Trend reporting", es: "Notificación de tendencias" },
];

export default function TechnicalFileTab({ project, version }) {
  const t = useT();
  const lang = (localStorage.getItem("rg_lang") || "en");
  if (!version) return <Empty msg={t("ov.no_analysis")} />;

  const intake = version.intake || {};
  const dev = intake.device_identity || {};
  const ip = intake.intended_purpose || {};
  const cl = intake.clinical_context || {};
  const ec = intake.existing_compliance || {};

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="grid-card p-5">
        <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-3">{lang === "es" ? "Anexo II — Documentación técnica" : "Annex II — Technical Documentation"}</div>
        <ul className="divide-y divide-zinc-100 text-sm">
          {ANNEX_II_KEYS.map((k) => (
            <li key={k.source} className="py-2 flex items-center justify-between">
              <span>{k[lang] || k.en}</span>
              <span className="font-mono text-[10px] text-emerald-600">✓</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="grid-card p-5">
        <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-3">{lang === "es" ? "Anexo III — Vigilancia post-comercialización" : "Annex III — Post-Market Surveillance"}</div>
        <ul className="divide-y divide-zinc-100 text-sm">
          {ANNEX_III_KEYS.map((k, i) => (
            <li key={k.en} className="py-2 flex items-center justify-between">
              <span>{k[lang] || k.en}</span>
              <span className="font-mono text-[10px] text-zinc-400">{cl.pmcf_plan === "yes" && i === 0 ? "✓" : "—"}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="grid-card p-5 lg:col-span-2" data-testid="technical-file-device-data">
        <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-3">{lang === "es" ? "Datos del dispositivo (v" : "Device data (v"}{version.version})</div>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm">
          <Row label={lang === "es" ? "Nombre" : "Name"} v={dev.name} />
          <Row label={lang === "es" ? "Fabricante" : "Manufacturer"} v={dev.manufacturer} />
          <Row label="Model" v={dev.model} />
          <Row label="UDI-DI" v={dev.udi_di} />
          <Row label={lang === "es" ? "Finalidad prevista" : "Intended purpose"} v={ip.description} full />
          <Row label={lang === "es" ? "Indicación" : "Indication"} v={ip.target_disease} />
          <Row label={lang === "es" ? "Zona anatómica" : "Anatomical site"} v={ip.body_part} />
          <Row label="ISO 14971" v={ec.iso_14971} />
          <Row label="IEC 62304" v={ec.iec_62304} />
          <Row label={lang === "es" ? "Predecesor con marca CE" : "CE-marked predecessor"} v={ec.ce_marked_predecessor} />
        </dl>
      </div>
      <div className="grid-card p-5 lg:col-span-2" data-testid="technical-file-qms-iso13485">
        <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2">{t("techfile.qms.title")}</div>
        <p className="mb-3 text-xs leading-5 text-zinc-500">{t("techfile.qms.note")}</p>
        <dl className="text-sm">
          <Row label={t("techfile.qms.iso13485")} v={ec.iso_13485} />
        </dl>
      </div>
    </div>
  );
}

function Row({ label, v, full }) {
  const isMissing = v === "Data not provided" || v === "" || v == null;
  return (
    <div className={`flex gap-3 py-1 border-b border-zinc-50 ${full ? "md:col-span-2" : ""}`}>
      <dt className="text-zinc-500 w-40 flex-shrink-0">{label}</dt>
      <dd className={`flex-1 ${isMissing ? "text-amber-600 italic" : "text-zinc-900"}`}>{isMissing ? (v || "—") : v}</dd>
    </div>
  );
}
